/**
 * WhatsApp-style media renderer for chat bubbles.
 *
 * - Inbound media: bytes streamed from the backend proxy `${mediaEndpoint}/:id`
 *   (which lazy-fetches from Meta on first request) → object URL → <img>/<video>.
 * - Outbound media with a public mediaUrl: rendered directly (no auth needed).
 * - Click image/video → opens a fullscreen lightbox (WhatsApp-style) with
 *   close (×), download, and Esc-to-close.
 * - Old inbound messages stored before media support shipped (no mediaId AND
 *   no mediaUrl) show a friendly placeholder instead of bare "[image]" text.
 */
import React, { useEffect, useRef, useState, useCallback } from "react";
import ReactDOM from "react-dom";
import {
  FileText, Download, Loader2, AlertCircle, X, ImageOff,
} from "lucide-react";
import apiClient from "../../utils/apiClient";
import "./whatsapp-media.scss";

/* ── Hook: fetch authenticated media as blob → object URL ───────────────── */
const useMediaBlob = (mediaEndpoint, messageId, enabled) => {
  const [src, setSrc] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const objectUrlRef = useRef("");

  useEffect(() => {
    if (!enabled || !messageId) return undefined;
    let cancelled = false;
    setLoading(true);
    setErr("");

    apiClient
      .get(`${mediaEndpoint}/${messageId}`, { responseType: "blob" })
      .then((res) => {
        if (cancelled) return;
        const url = URL.createObjectURL(res.data);
        objectUrlRef.current = url;
        setSrc(url);
      })
      .catch(async (e) => {
        if (cancelled) return;
        // axios + responseType:blob → error body is also a Blob; parse it.
        let msg = "Failed to load media";
        try {
          if (e?.response?.data instanceof Blob) {
            const txt = await e.response.data.text();
            try { msg = JSON.parse(txt).message || msg; } catch { msg = txt || msg; }
          } else {
            msg = e?.response?.data?.message || e?.message || msg;
          }
        } catch { /* keep default */ }
        setErr(msg);
      })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => {
      cancelled = true;
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = "";
      }
    };
  }, [messageId, mediaEndpoint, enabled]);

  return { src, err, loading };
};

/* ── Lightbox (rendered via portal) ─────────────────────────────────────── */
const Lightbox = ({ src, mimeType, title, caption, onClose, downloadName }) => {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose]);

  const handleDownload = () => {
    const a = document.createElement("a");
    a.href = src;
    a.download = downloadName || "download";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const isVideo = (mimeType || "").startsWith("video/");

  return ReactDOM.createPortal(
    <div className="wa-lightbox" onClick={onClose} role="dialog" aria-modal="true">
      <div className="wa-lightbox__topbar" onClick={(e) => e.stopPropagation()}>
        <span className="wa-lightbox__title">{title || ""}</span>
        <div className="wa-lightbox__actions">
          <button className="wa-lightbox__btn" onClick={handleDownload} title="Download" aria-label="Download">
            <Download size={18} />
          </button>
          <button className="wa-lightbox__btn" onClick={onClose} title="Close" aria-label="Close">
            <X size={20} />
          </button>
        </div>
      </div>

      <div className="wa-lightbox__content" onClick={(e) => e.stopPropagation()}>
        {isVideo ? (
          <video src={src} controls autoPlay />
        ) : (
          <img src={src} alt={title || "Media"} />
        )}
      </div>

      {caption && <div className="wa-lightbox__caption">{caption}</div>}
    </div>,
    document.body,
  );
};

/* ── Helpers ────────────────────────────────────────────────────────────── */
const formatBytes = (n) => {
  if (!n && n !== 0) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
};

const PLACEHOLDER_LABEL = {
  image: "Image not available",
  sticker: "Sticker not available",
  video: "Video not available",
  audio: "Audio not available",
  document: "File not available",
};

/* ── Main component ─────────────────────────────────────────────────────── */
const WhatsAppMediaMessage = ({ message, mediaEndpoint }) => {
  const type = message.messageType;
  const hasMedia = !!(message.mediaId || message.mediaUrl);

  // Outbound messages may carry a public URL we can render directly.
  const directUrl =
    message.direction === "outbound" && message.mediaUrl && /^https?:\/\//.test(message.mediaUrl)
      ? message.mediaUrl
      : "";

  const { src: blobSrc, err, loading } = useMediaBlob(
    mediaEndpoint,
    message._id,
    hasMedia && !directUrl,
  );
  const src = directUrl || blobSrc;

  const [lightboxOpen, setLightboxOpen] = useState(false);
  const openLightbox = useCallback(() => { if (src) setLightboxOpen(true); }, [src]);
  const closeLightbox = useCallback(() => setLightboxOpen(false), []);

  /* ── No media at all (legacy message) ─────────────────────────────── */
  if (!hasMedia && type !== "text" && type !== "template") {
    return (
      <div className="wa-media__placeholder">
        <ImageOff size={16} />
        <span>{PLACEHOLDER_LABEL[type] || `${type} not available`}</span>
      </div>
    );
  }

  /* ── Text/template: caller renders directly ───────────────────────── */
  if (!hasMedia) return null;

  /* ── Error / loading states ───────────────────────────────────────── */
  if (err) {
    return (
      <div className="wa-media__error">
        <AlertCircle size={14} /> {err}
      </div>
    );
  }
  if (loading && !src) {
    return (
      <div className="wa-media__loading">
        <Loader2 size={16} className="wai-spin" /> Loading…
      </div>
    );
  }
  if (!src) return null;

  /* ── Image ────────────────────────────────────────────────────────── */
  if (type === "image") {
    return (
      <>
        <button
          type="button"
          className="wa-media__image-btn"
          onClick={openLightbox}
          aria-label="Open image"
        >
          <img src={src} alt={message.content || "Image"} loading="lazy" />
        </button>
        {lightboxOpen && (
          <Lightbox
            src={src}
            mimeType={message.mediaMimeType || "image/jpeg"}
            title="Photo"
            caption={message.content}
            onClose={closeLightbox}
            downloadName={message.mediaFilename || `image-${message._id}.jpg`}
          />
        )}
      </>
    );
  }

  /* ── Sticker ──────────────────────────────────────────────────────── */
  if (type === "sticker") {
    return (
      <>
        <img
          src={src}
          alt="Sticker"
          className="wa-media__sticker"
          onClick={openLightbox}
        />
        {lightboxOpen && (
          <Lightbox
            src={src}
            mimeType={message.mediaMimeType || "image/webp"}
            title="Sticker"
            onClose={closeLightbox}
            downloadName={`sticker-${message._id}.webp`}
          />
        )}
      </>
    );
  }

  /* ── Video ────────────────────────────────────────────────────────── */
  if (type === "video") {
    return (
      <>
        <video
          className="wa-media__video"
          controls
          preload="metadata"
        >
          <source src={src} type={message.mediaMimeType || "video/mp4"} />
        </video>
        {lightboxOpen && (
          <Lightbox
            src={src}
            mimeType={message.mediaMimeType || "video/mp4"}
            title="Video"
            caption={message.content}
            onClose={closeLightbox}
            downloadName={message.mediaFilename || `video-${message._id}.mp4`}
          />
        )}
      </>
    );
  }

  /* ── Audio ────────────────────────────────────────────────────────── */
  if (type === "audio") {
    return <audio className="wa-media__audio" controls src={src} />;
  }

  /* ── Document ─────────────────────────────────────────────────────── */
  if (type === "document") {
    return (
      <a
        href={src}
        download={message.mediaFilename || "document"}
        target="_blank"
        rel="noreferrer"
        className="wa-media__doc"
      >
        <span className="wa-media__doc-icon"><FileText size={18} /></span>
        <span className="wa-media__doc-info">
          <span className="wa-media__doc-name">
            {message.mediaFilename || "Document"}
          </span>
          <span className="wa-media__doc-meta">
            {message.mediaMimeType || ""} {formatBytes(message.size)}
          </span>
        </span>
        <Download size={16} />
      </a>
    );
  }

  return null;
};

export default WhatsAppMediaMessage;
